import { useCallback } from 'react';
import { MediaSegment } from 'redux/modules/entities';
import {
  addGapsToTime,
  removeGapsFromTime,
  skipGaps,
} from 'utils/embed/media-segment';
import { millisToSec, secToMillis } from 'utils/time';
import {
  getMediaTime,
  seekToFrameAccurate as seekToFrameAccurateUtil,
  waitForSeeked,
} from 'utils/video';

export type UseFrameAccurateSeekProps = {
  onSkipGap?: () => void;
  segments: MediaSegment[];
  videoRef: React.RefObject<HTMLVideoElement>;
};

function logicalToPhysicalTime(
  logicalSec: number,
  segments: MediaSegment[],
): number {
  return millisToSec(addGapsToTime(secToMillis(logicalSec), segments));
}

function physicalToLogicalTime(
  physicalSec: number,
  segments: MediaSegment[],
): number {
  return millisToSec(removeGapsFromTime(secToMillis(physicalSec), segments));
}

/**
 * Frame-accurate seek for SegmentedVideoPlayer.
 * Wraps the generic frame-accurate seek utility with segmentation logic
 */
async function seekToFrameAccurateInternal(
  video: HTMLVideoElement,
  targetLogicalSec: number,
  segments: MediaSegment[],
  onSkipGap?: () => void,
): Promise<number> {
  // convert logical target to physical time
  const targetPhysicalSec = logicalToPhysicalTime(targetLogicalSec, segments);

  // let utility do initial seek
  let currentFrameTime = await seekToFrameAccurateUtil(
    video,
    targetPhysicalSec,
  );

  // ensure we're in a valid segment (not in a gap)
  const currentFrameTimeMillis = secToMillis(currentFrameTime);
  const validFrameTimeMillis = skipGaps(currentFrameTimeMillis, segments);
  const validFrameTimeSec = millisToSec(validFrameTimeMillis);

  // if skipGaps moved us, we're in a gap - seek to a valid position
  if (validFrameTimeSec !== currentFrameTime) {
    onSkipGap?.();
    video.currentTime = validFrameTimeSec;
    await waitForSeeked(video);

    // re-run frame-accurate seek from valid position
    currentFrameTime = await seekToFrameAccurateUtil(video, targetPhysicalSec);

    // final validation after re-seeking
    const finalFrameTimeMillis = secToMillis(currentFrameTime);
    const finalValidMillis = skipGaps(finalFrameTimeMillis, segments);
    const finalValidSec = millisToSec(finalValidMillis);

    if (finalValidSec !== currentFrameTime) {
      // still in a gap after re-seeking - just use the valid position
      onSkipGap?.();
      video.currentTime = finalValidSec;
      await waitForSeeked(video);
      currentFrameTime = await getMediaTime(video);
    }
  }

  // convert back to logical
  const actualLogicalSec = physicalToLogicalTime(currentFrameTime, segments);

  return actualLogicalSec;
}

export default function useFrameAccurateSeek({
  onSkipGap,
  segments,
  videoRef,
}: UseFrameAccurateSeekProps) {
  const seekToFrameAccurate = useCallback(
    async (logicalTimeSec: number): Promise<number> => {
      const video = videoRef.current;

      if (!video) {
        throw new Error('Video not found');
      }

      return seekToFrameAccurateInternal(
        video,
        logicalTimeSec,
        segments,
        onSkipGap,
      );
    },
    [segments, videoRef, onSkipGap],
  );

  return seekToFrameAccurate;
}
